import { createFileRoute } from "@tanstack/react-router";
import { GrowthPlanSections } from "@/components/GrowthPlanSections";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    "q": "How do law firms get more case inquiries?",
    "a": "Practice-area-specific pages — not one generic 'services' page. 'Personal injury lawyer Denver', 'DUI attorney Colorado Springs', 'estate planning Lakewood' each rank separately. Multi-practice firms need a page for every area."
  },
  {
    "q": "Is SEO worth it compared to legal directories?",
    "a": "Yes — your own SEO compounds; directory spend doesn't. Directories rank well, but you're paying to be one of 20 lawyers on the page. Your own site captures the entire click."
  },
  {
    "q": "Should law firms run Google Ads?",
    "a": "Personal injury and DUI yes — high CPCs but high case value. Family and estate planning are profitable too. We build practice-specific campaigns with call tracking and recording."
  },
  {
    "q": "How does this work for multi-practice firms?",
    "a": "Every practice area gets its own page, its own SEO, and its own ads. A firm that does PI, family, and estate planning runs three parallel pipelines instead of one diluted one."
  },
  {
    "q": "Do you handle attorney bio pages and case results?",
    "a": "Yes — attorney pages, case result pages, and FAQ pages are part of the standard build. They're often the highest-converting pages on a law firm site."
  }
];

export const Route = createFileRoute("/law-firm-marketing-colorado")({
  head: () =>
    industryMarketingHead("Law Firm", "law firm", "law-firm-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry={"Law Firm"}
      industryLower={"law firm"}
      slug={"law-firm-marketing-colorado"}
      subheadline={"We help Colorado law firms — solo practitioners through mid-size firms — build a steady pipeline of qualified case inquiries across every practice area."}
      painPoints={["Directory listings cost money every month but rarely bring signed cases","Your site doesn't have practice-area pages — just one 'services' page","Other firms outrank you in your own city for your own practice areas"]}
      serviceDescriptions={{
        seo: "Rank for every practice area — personal injury, criminal defense, family law, estate planning, business, and more.",
        googleMaps: "Own 'lawyer near me' and practice-area Map Pack across every Colorado market.",
        webDesign: "Practice-area landing pages with case results, attorney bios, and intake forms tuned to convert.",
        ppc: "Profitable Google Ads tuned to high-intent legal queries with bulletproof tracking and call recording.",
        geoAi: "Get cited when prospects ask ChatGPT 'best personal injury lawyer in Denver' or 'family law attorney near me'.",
        socialMedia: "Authority-building content that converts referrals and warm intros into signed clients.",
      }}
      testimonial={{"quote":"We built our own steady flow of case inquiries. Three months in, inbound case value covered the whole engagement.","role":"Managing Partner","city":"Denver"}}
      faqs={FAQS}
      extraSections={<GrowthPlanSections
          quoteIndustry="Law Firm"
          eyebrow="Client acquisition"
          tipsHeading="How Colorado law firms win more case inquiries"
          tips={[
            { title: "One page per practice area", body: "Personal injury, family, DUI and estate planning each need their own page to show up in search." },
            { title: "Answer the phone fast", body: "People with legal problems call the first firm that picks up. A click-to-call button and fast follow-up win cases." },
            { title: "Show your attorneys", body: "Real bios, photos and bar details build trust before the first call." },
            { title: "Steady Google reviews", body: "Fresh reviews keep your firm in the top three on Google Maps in your city." },
            { title: "Plain-language answers", body: "Clear answers to common legal questions help ChatGPT and Google name your firm." },
            { title: "Track every inquiry", body: "Know which pages and searches bring signed cases, then grow what works." },
          ]}
          planHeading="Your 90-day local SEO plan for a Colorado law firm"
          plan={[
            { label: "Days 1–30", title: "Fix the foundation", items: ["Google Business Profile categories, hours, photos and practice areas", "Fast, mobile-friendly site with a call button up top", "Name, address and phone matched across directories"] },
            { label: "Days 31–60", title: "Build practice-area pages", items: ["A page for each practice area you want more of", "Pages for the Colorado towns your clients come from", "Common client questions answered in plain words"] },
            { label: "Days 61–90", title: "Grow trust and high-value cases", items: ["Review requests after closed matters", "Weekly Google Business Profile posts", "Track calls and inquiries, then double down"] },
          ]}
          caseStudySlugs={["cleaning-fort-collins", "roofing-denver"]}
          caseStudyNote="We don't have a published law firm case study yet. Here are the closest real results: a small local business that outranked national brands on a small budget, and a high-value campaign where every dollar was tracked to booked work."
          links={[
            { to: "/learn/tips/law-firm-marketing-tips-colorado", label: "Law firm marketing tips for Colorado" },
            { to: "/law-firm-web-design-colorado-springs", label: "Law firm web design in Colorado Springs" },
            { to: "/web-design-castle-rock-colorado", label: "Castle Rock web design company" },
            { to: "/pricing", label: "Pricing" },
          ]}
        />}
      cityHubLinks={[{"to":"/colorado-springs","label":"Law firm marketing in Colorado Springs","description":"Compete with the major Colorado Springs firms across every practice area."},{"to":"/denver-digital-marketing","label":"Law firm marketing in Denver","description":"Win cases in Denver's competitive legal market."},{"to":"/boulder-digital-marketing","label":"Law firm marketing in Boulder","description":"Build pipeline in Boulder's specialized legal and business market."}]}
    />
  ),
});
